import React, { useEffect, useRef, useState, useMemo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DocumentationProvider, useDocumentation } from '../context/DocumentationContext';
import '../styles/Documentation.css';

const DocumentationContent = () => {
  const {
    documentationData,
    activeSection,
    setActiveSection,
    activeTopic,
    setActiveTopic,
    getCurrentTopic,
    getNavigationTopics
  } = useDocumentation();

  const contentRef = useRef(null);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [headingsReady, setHeadingsReady] = useState(false);
  const currentTopic = getCurrentTopic();
  const navigation = getNavigationTopics();

  // Extract headings from content for Table of Contents
  const headings = useMemo(() => {
    if (!currentTopic?.content) return [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentTopic.content;
    const headingElements = tempDiv.querySelectorAll('h1, h2');
    return Array.from(headingElements).map((heading, index) => ({
      id: `heading-${index}`,
      text: heading.textContent,
      level: heading.tagName
    }));
  }, [currentTopic]);

  // Scroll to top when topic changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTopic, activeSection]);

  // Set first heading as active when headings change
  useEffect(() => {
    if (headings.length > 0) {
      setTimeout(() => {
        setActiveHeadingId(headings[0].id);
      }, 300);
    }
  }, [headings]);

  const handleTopicChange = (sectionId, topicId) => {
    setActiveSection(sectionId);
    setActiveTopic(topicId);
  };

  const handleHeadingClick = (headingIndex) => {
    const contentDiv = document.querySelector('.documentation-html-content');
    if (!contentDiv) return;
    
    const domHeadings = contentDiv.querySelectorAll('h1, h2');
    const element = domHeadings[headingIndex];
    
    if (element) {
      const offset = 80; // Navbar height offset + some padding
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Set as active immediately using the ID
      const headingId = element.id || `heading-${headingIndex}`;
      setActiveHeadingId(headingId);
    }
  };

  const handleNavigation = (topic) => {
    if (topic) {
      handleTopicChange(topic.sectionId, topic.id);
    }
  };

  // Add IDs to headings in content
  useEffect(() => {
    if (!currentTopic) {
      setHeadingsReady(false);
      return;
    }

    setHeadingsReady(false);

    // Wait for content to render
    const timer = setTimeout(() => {
      const contentDiv = document.querySelector('.documentation-html-content');
      if (contentDiv) {
        const h1Elements = contentDiv.querySelectorAll('h1, h2');
        h1Elements.forEach((heading, index) => {
          heading.id = `heading-${index}`;
        });
        
        // Mark headings as ready
        setTimeout(() => {
          setHeadingsReady(true);
        }, 100);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [currentTopic]);

  // Track active heading on scroll
  useEffect(() => {
    if (headings.length === 0 || !headingsReady) {
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset from top

      // Get all headings directly from DOM to avoid closure issues
      const contentDiv = document.querySelector('.documentation-html-content');
      if (!contentDiv) return;

      const domHeadings = contentDiv.querySelectorAll('h1, h2');
      
      // Get all heading positions
      const headingPositions = Array.from(domHeadings).map((element, index) => {
        const rect = element.getBoundingClientRect();
        const absoluteTop = rect.top + window.scrollY;
        return {
          id: element.id || `heading-${index}`,
          text: element.textContent,
          top: absoluteTop
        };
      }).filter(h => h.id);

      // Find the active heading
      let activeId = headingPositions[0]?.id || '';
      
      for (let i = 0; i < headingPositions.length; i++) {
        if (scrollPosition >= headingPositions[i].top) {
          activeId = headingPositions[i].id;
        } else {
          break;
        }
      }

      setActiveHeadingId(activeId);
    };

    // Initial check after DOM is ready
    const timer = setTimeout(() => {
      handleScroll();
    }, 100);

    // Add scroll listener with throttle
    let rafId = null;
    const scrollListener = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };

    window.addEventListener('scroll', scrollListener, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', scrollListener);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [headingsReady]);

  return (
    <>
      <Navbar />
      <div className="documentation-container">
        {/* Left Sidebar - Sections */}
        <aside className="documentation-sidebar left">
          <h3 className="sidebar-title">Documentation</h3>
          <nav className="sections-nav">
            {documentationData.map((section) => (
              <div key={section.id} className="section-group">
                <div
                  className={`section-header ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => handleTopicChange(section.id, section.topics[0].id)}
                >
                  <span className="section-title">{section.title}</span>
                </div>
                {activeSection === section.id && (
                  <ul className="topics-list">
                    {section.topics.map((topic) => (
                      <li
                        key={topic.id}
                        className={`topic-item ${activeTopic === topic.id ? 'active' : ''}`}
                        onClick={() => setActiveTopic(topic.id)}
                      >
                        {topic.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="documentation-main" ref={contentRef}>
          {currentTopic && (
            <>
              <article className="documentation-article">
                <div 
                  className="documentation-html-content"
                  dangerouslySetInnerHTML={{ __html: currentTopic.content }}
                />
              </article>

              {/* Previous/Next Navigation */}
              <nav className="documentation-navigation">
                <div className="nav-buttons">
                  {navigation.previous ? (
                    <button
                      className="nav-button prev"
                      onClick={() => handleNavigation(navigation.previous)}
                    >
                      <span className="nav-arrow">←</span>
                      <div className="nav-text">
                        <span className="nav-label">Previous</span>
                        <span className="nav-title">{navigation.previous.title}</span>
                      </div>
                    </button>
                  ) : (
                    <div></div>
                  )}
                  
                  {navigation.next ? (
                    <button
                      className="nav-button next"
                      onClick={() => handleNavigation(navigation.next)}
                    >
                      <div className="nav-text">
                        <span className="nav-label">Next</span>
                        <span className="nav-title">{navigation.next.title}</span>
                      </div>
                      <span className="nav-arrow">→</span>
                    </button>
                  ) : (
                    <div></div>
                  )}
                </div>
              </nav>
            </>
          )}
        </main>

        {/* Right Sidebar - Table of Contents */}
        <aside className="documentation-sidebar right">
          <h4 className="toc-title">On This Page</h4>
          <nav className="table-of-contents">
            {headings.map((heading, index) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                className={`toc-link ${heading.level.toLowerCase()} ${activeHeadingId === heading.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleHeadingClick(index);
                }}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </aside>
      </div>
      <Footer />
    </>
  );
};

const Documentation = () => {
  return (
    <DocumentationProvider>
      <DocumentationContent />
    </DocumentationProvider>
  );
};

export default Documentation;

